import React from "react";
import Grid from "@mui/material/Grid";
import { ThemeProvider } from "@mui/material/styles";
import { useExpertOnboardingContext, useExpertOnboardingMetrics, useExpertTaskListContext } from "@limitless/expert";
import { useTranslation } from "@limitless/common/hooks";
import { useHistory } from "react-router-dom";

import { usePageViewRecorder } from "@limitless/analytics";
import { useNavigation } from "@limitless/portal";
import useCustomTheme from "../../utils/hooks/useCustomTheme";
import Chip from "@limitless/modern/data-display/chip/Chip";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@limitless/modern/data-display/typography/Typography";
import CircularProgress from "@mui/material/CircularProgress";

const useStyles = makeStyles(theme => ({
    chip: {
        backgroundColor: theme.palette.secondary.light,
        minWidth: 57,
        borderRadius: "8px",
        whiteSpace: "nowrap",
        marginBottom: "10px",
        marginLeft: "20px"
    },
    row2Text: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        color: theme.palette.primary.main,
        paddingRight: "35px",
        fontWeight: 500,
        paddingBottom: theme.spacing(2),
        marginTop: "auto"
    },
    card: {
        marginLeft: "12px",
        width: "330px",
        height: 140,
        borderRadius: 16,
        boxShadow: "0px 3px 8px -4px rgba(0, 0, 0, 0.3)",
        backgroundColor: theme.palette.background.elevation1,
        marginBottom: 24,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
    },
    cardTitle: {
        margin: "24px 32px",
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: "100px"
    }
}));

const ExpertPracticeQuestionsOverview = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { navigate } = useNavigation();
    const theme = useCustomTheme();
    const { selectMessage } = useExpertOnboardingContext();
    const {
        tasklist: { messages }
    } = useExpertTaskListContext();
    const { onboardingDetail } = useExpertOnboardingMetrics(t);
    const nav = useHistory();

    const [loadingTaskId, setLoadingTaskId] = React.useState(null);

    usePageViewRecorder("Onboarding - Practice Tasks");

    const handleViewTask = task => {
        setLoadingTaskId(task.messageId);

        if (messages[task.messageId] || !task.primaryResponseDialogueId) {
            nav.push({
                pathname: "/tasklist",
                state: {
                    task: messages[task.messageId]
                }
            });
        } else {
            selectMessage(task, hasError => {
                if (hasError) {
                    navigate("/tasklist");
                }
                setLoadingTaskId(null);
            });
        }
    };

    const allTasks = onboardingDetail?.onboarding?.activePracticeQuestions || [];

    const visibleReviewStates = new Set(allTasks.map(task => task.reviewState));

    const PQStates = [
        { label: "To do", id: "todo", reviewState: "not_scheduled", buttonText: "Respond" },
        { label: "Awaiting review", id: "awaitingReview", reviewState: "in_progress" },
        { label: "Reviewed", id: "reviewed", reviewState: "complete", buttonText: "Feedback" }
    ].map(state => ({
        ...state,
        visibility: visibleReviewStates.has(state.reviewState)
    }));

    return (
        <ThemeProvider theme={theme}>
            <Grid
                container
                wrap="nowrap"
                sx={{
                    marginBottom: 4,
                    overflowX: "auto",
                    justifyContent: "space-between"
                }}
            >
                {PQStates.map((item, key) => (
                    <Grid
                        key={key}
                        item
                        sx={{
                            flex: "0 0 304px"
                        }}
                    >
                        <Chip className={classes.chip} label={t(item.label)} />

                        {allTasks
                            .filter(task => task.reviewState === item.reviewState)
                            .map((task, idx) => (
                                <Grid key={idx} className={classes.card}>
                                    <Grid item className={classes.cardTitle}>
                                        <Typography color={theme => theme.palette.text.primary} variant="body2">
                                            {task.body}
                                        </Typography>
                                    </Grid>
                                    {item.buttonText && (
                                        <Grid
                                            onClick={() => {
                                                if (!loadingTaskId) {
                                                    handleViewTask(task);
                                                }
                                            }}
                                            style={{ cursor: loadingTaskId ? "default" : "pointer" }}
                                        >
                                            <Typography variant="body2" className={classes.row2Text}>
                                                {loadingTaskId === task.messageId ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    item.buttonText
                                                )}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            ))}
                    </Grid>
                ))}
            </Grid>
        </ThemeProvider>
    );
};

export default ExpertPracticeQuestionsOverview;
